import { useCallback, useEffect, useMemo, useState } from 'react'
import EmptyState from './EmptyState'
import LoadingPlaceholder from './LoadingPlaceholder'
import { getCoreMemory, updateCoreMemory } from '../api/memory'
import { formatUtcDateTime } from '../utils/timezone'

type MemoryEntities = {
  people?: string[]
  accounts?: string[]
  products?: string[]
  locations?: string[]
  campaigns?: string[]
}

type MemoryGoals = {
  daily?: string[]
  weekly?: string[]
}

type MemoryItem = {
  summary?: string
  decisions?: string[]
  entities?: MemoryEntities
  goals?: MemoryGoals
  strategy_plans?: string[]
  quotas?: string[]
  incentives_merch?: string[]
  tags?: string[]
  timestamps?: string[]
  generated_at?: string
}

type CoreMemoryData = {
  user_id?: string
  memories?: Array<MemoryItem | { memory: MemoryItem }>
}

type Status = 'loading' | 'error' | 'success'

type CoreMemoryProps = {
  userId: string
}

const toStringList = (value: unknown) => {
  if (!Array.isArray(value)) return []
  return value.flatMap((item) => {
    const coerced = typeof item === 'string' ? item : item ? String(item) : ''
    const trimmed = coerced.trim()
    return trimmed ? [trimmed] : []
  })
}

const isMemoryWrapper = (entry: MemoryItem | { memory: MemoryItem }): entry is { memory: MemoryItem } =>
  typeof entry === 'object' && entry !== null && 'memory' in entry

const extractMemory = (entry: MemoryItem | { memory: MemoryItem } | null | undefined): MemoryItem | null => {
  if (!entry) return null
  if (isMemoryWrapper(entry)) return entry.memory
  return entry
}

const normalizeMemories = (value?: CoreMemoryData['memories']) => {
  if (!Array.isArray(value)) return []
  const parsed = value.flatMap((entry) => { const r = extractMemory(entry); return r ? [r] : [] })
  const parseDate = (input?: string) => {
    if (!input) return 0
    const parsedDate = Date.parse(input)
    return Number.isNaN(parsedDate) ? 0 : parsedDate
  }
  return parsed.toSorted((a, b) => parseDate(b.generated_at) - parseDate(a.generated_at))
}

function CoreMemory({ userId }: CoreMemoryProps) {
  const [status, setStatus] = useState<Status>('loading')
  const [coreMemory, setCoreMemory] = useState<CoreMemoryData | null>(null)
  const [workingMemories, setWorkingMemories] = useState<MemoryItem[]>([])
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [saving, setSaving] = useState(false)
  const [retryCount, setRetryCount] = useState(0)

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      setStatus('loading')
      setErrorMessage(null)
      try {
        const data = await getCoreMemory(userId) as unknown as CoreMemoryData
        if (cancelled) return
        setCoreMemory(data)
        setWorkingMemories(normalizeMemories(data?.memories))
        setHasUnsavedChanges(false)
        setSaveError(null)
        setStatus('success')
      } catch (err) {
        if (cancelled) return
        console.error('Failed to load core memory', err)
        setCoreMemory(null)
        setWorkingMemories([])
        setErrorMessage(err instanceof Error ? err.message : 'Unable to load core memory.')
        setStatus('error')
      }
    }

    load()
    return () => { cancelled = true }
  }, [userId, retryCount])

  const handleRetry = useCallback(() => {
    setRetryCount((c) => c + 1)
  }, [])

  const memories = useMemo(() => [...workingMemories], [workingMemories])

  const handleDeleteMemory = (index: number) => {
    setWorkingMemories((prev) => prev.filter((_, idx) => idx !== index))
    setHasUnsavedChanges(true)
  }

  const handleSave = async () => {
    if (saving) return
    try {
      setSaving(true)
      setSaveError(null)
      const nextCoreMemory = {
        ...(coreMemory ?? {}),
        user_id: userId,
        memories: workingMemories,
      }
      await updateCoreMemory(userId, nextCoreMemory as unknown as Parameters<typeof updateCoreMemory>[1])
      setCoreMemory(nextCoreMemory)
      setHasUnsavedChanges(false)
    } catch (err) {
      console.error('Failed to save core memory', err)
      setSaveError(err instanceof Error ? err.message : 'Unable to save core memory.')
    } finally {
      setSaving(false)
    }
  }

  if (status === 'loading') {
    return (
      <div data-testid="core-memory-loading">
        <LoadingPlaceholder variant="week" />
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div
        data-testid="core-memory-error"
        className="muted archive-error"
        style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'flex-start' }}
      >
        <span>{errorMessage || 'Unable to load core memory.'}</span>
        <button type="button" className="secondary-button" onClick={handleRetry}>
          Retry
        </button>
      </div>
    )
  }

  // status === 'success'
  return (
    <>
      {saveError && <div className="muted archive-error">{saveError}</div>}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap',
          marginBottom: '12px',
        }}
      >
        <div className="pill-row">
          <span className="pill" data-testid="core-memory-count">{memories.length} memories</span>
        </div>
        {hasUnsavedChanges ? (
          <button type="button" className="primary-button" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        ) : null}
      </div>

      {memories.length === 0 ? (
        <div data-testid="core-memory-empty">
          <EmptyState
            icon="🧠"
            title="No memories yet"
            description="Core memory will appear once the assistant stores key facts and decisions."
            tags={['Waiting for first memory', 'Ask the assistant to remember']}
          />
        </div>
      ) : (
        <div className="stack" data-testid="core-memory-list" style={{ minHeight: '50vh' }}>
          {memories.map((memory, pos) => {
            const summary = memory.summary?.trim() || 'Memory'
            const decisions = toStringList(memory.decisions)
            const tags = toStringList(memory.tags)
            const timestamps = toStringList(memory.timestamps)
            const incentives = toStringList(memory.incentives_merch)
            const quotas = toStringList(memory.quotas)
            const strategyPlans = toStringList(memory.strategy_plans)
            const dailyGoals = toStringList(memory.goals?.daily)
            const weeklyGoals = toStringList(memory.goals?.weekly)
            const entities = memory.entities
            const entityGroups: Array<[string, string[]]> = entities
              ? [
                  ['People', toStringList(entities.people)],
                  ['Accounts', toStringList(entities.accounts)],
                  ['Products', toStringList(entities.products)],
                  ['Locations', toStringList(entities.locations)],
                  ['Campaigns', toStringList(entities.campaigns)],
                ]
              : []
            const hasEntities = entityGroups.some(([, items]) => items.length)
            const { date, time } = formatUtcDateTime(memory.generated_at)
            const dateLabel = date === '—' ? 'Unknown date' : date
            return (
              <div key={`memory-${pos}`} className="card">
                <div className="list-meta">
                  <span className="pill pill-strong">{dateLabel}</span>
                  {time ? <span className="pill muted">{time}</span> : null}
                  {tags.length ? <span className="pill">{tags.length} tags</span> : null}
                  {decisions.length ? <span className="pill">{decisions.length} decisions</span> : null}
                  <button
                    type="button"
                    className="ghost-button small danger list-meta-actions"
                    onClick={() => handleDeleteMemory(pos)}
                  >
                    Delete
                  </button>
                </div>
                <div className="tile-title">{summary}</div>
                {decisions.length ? (
                  <div className="muted" style={{ marginTop: '8px' }}>
                    Decisions: {decisions.join(' • ')}
                  </div>
                ) : null}
                {incentives.length ? (
                  <div className="muted" style={{ marginTop: '8px' }}>
                    Incentives: {incentives.join(' • ')}
                  </div>
                ) : null}
                {quotas.length ? (
                  <div className="muted" style={{ marginTop: '8px' }}>
                    Quotas: {quotas.join(' • ')}
                  </div>
                ) : null}
                {strategyPlans.length ? (
                  <div className="muted" style={{ marginTop: '8px' }}>
                    Strategy plans: {strategyPlans.join(' • ')}
                  </div>
                ) : null}
                {dailyGoals.length ? (
                  <div className="muted" style={{ marginTop: '8px' }}>
                    Daily goals: {dailyGoals.join(' • ')}
                  </div>
                ) : null}
                {weeklyGoals.length ? (
                  <div className="muted" style={{ marginTop: '8px' }}>
                    Weekly goals: {weeklyGoals.join(' • ')}
                  </div>
                ) : null}
                {timestamps.length ? (
                  <div className="muted" style={{ marginTop: '8px' }}>
                    Time refs: {timestamps.join(' • ')}
                  </div>
                ) : null}
                {hasEntities ? (
                  <div className="muted" style={{ marginTop: '8px' }}>
                    {entityGroups
                      .flatMap(([label, items]) => items.length ? [`${label}: ${items.join(', ')}`] : [])
                      .join(' | ')}
                  </div>
                ) : null}
                {tags.length ? (
                  <div className="pill-row" style={{ marginTop: '10px' }}>
                    {tags.map((tag) => (
                      <span key={`${tag}-${pos}`} className="pill muted">
                        #{tag}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
            )
          })}
        </div>
      )}
    </>
  )
}

export default CoreMemory
